import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Field,
  Input,
  Modal,
  ModalActions,
  Select,
  Status,
} from "@fwf/fwfa-design-system";
import { providerLabel, workspaceDisplayName } from "@web/lib/workspace-display";
import { parseJson, normalizeProjectOptions } from "@web/lib/api";
import type { ProjectOption } from "@web/lib/onboarding";

type WorkspaceProvider = "sprites" | "gcp_compute" | "e2b" | "daytona" | "lambda_microvms";

type WorkspaceRef = {
  provider: WorkspaceProvider;
  resourceId: string;
  projectIds: string[];
  ownerUserId: string;
  shared: boolean;
  updatedAt: number;
  meta?: Record<string, unknown>;
};

type Props = {
  visible: boolean;
  onClose: () => void;
  onConnected: (workspace: WorkspaceRef) => void;
  providerMode?: WorkspaceProvider;
};

type Tab = "mine" | "project" | "create";

export default function TeamWorkspaceDialog({
  visible,
  onClose,
  onConnected,
  providerMode = "daytona",
}: Props) {
  const [tab, setTab] = useState<Tab>("mine");
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [query, setQuery] = useState("");
  const [providerFilter, setProviderFilter] = useState<"all" | WorkspaceProvider>("all");
  const [mine, setMine] = useState<WorkspaceRef[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState<WorkspaceRef | null>(null);
  const [searchResults, setSearchResults] = useState<WorkspaceRef[]>([]);
  const [createProvider, setCreateProvider] = useState<WorkspaceProvider>(
    providerMode,
  );
  const [createShared, setCreateShared] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const projectForRequests = selectedProject || projects[0]?.id || "";
  const availableProviders = useMemo<WorkspaceProvider[]>(
    () => [providerMode],
    [providerMode],
  );
  const effectiveProviderFilter = availableProviders.length === 1
    ? availableProviders[0]
    : providerFilter;

  const load = useCallback(async () => {
    if (!visible) return;
    setLoading(true);
    setError(null);

    try {
      const projectResp = await fetch("/api/projects", { credentials: "include" });
      const projectData = await parseJson<{ projects: unknown }>(projectResp);
      const nextProjects = normalizeProjectOptions(projectData.projects);
      setProjects(nextProjects);
      setSelectedProject((current) => current || nextProjects[0]?.id || "");

      const mineResp = await fetch("/api/workspaces/mine", { credentials: "include" });
      const mineData = await parseJson<{ selected: WorkspaceRef | null; workspaces: WorkspaceRef[] }>(mineResp);
      const nextSelectedWorkspace = mineData.selected || null;
      const nextMine = Array.isArray(mineData.workspaces) ? mineData.workspaces : [];
      setSelectedWorkspace(nextSelectedWorkspace);
      setMine(nextMine);
      setTab(nextMine.length > 0 || nextSelectedWorkspace ? "mine" : "create");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load workspaces");
    } finally {
      setLoading(false);
    }
  }, [visible]);

  const loadSearch = useCallback(async () => {
    if (!visible) return;
    if (tab !== "project") {
      setSearchResults([]);
      return;
    }

    const params = new URLSearchParams();
    if (projectForRequests) params.set("projectId", projectForRequests);
    if (query.trim()) params.set("q", query.trim());
    if (effectiveProviderFilter !== "all") params.set("provider", effectiveProviderFilter);

    try {
      const response = await fetch(`/api/workspaces/search?${params.toString()}`, {
        credentials: "include",
      });
      const data = await parseJson<{ workspaces: WorkspaceRef[] }>(response);
      setSearchResults(Array.isArray(data.workspaces) ? data.workspaces : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to search workspaces");
    }
  }, [effectiveProviderFilter, projectForRequests, query, tab, visible]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadSearch();
  }, [loadSearch]);

  useEffect(() => {
    setProviderFilter((current) => {
      if (availableProviders.length === 1) return availableProviders[0];
      return current === "all" || availableProviders.includes(current) ? current : "all";
    });
    setCreateProvider((current) => (
      availableProviders.includes(current) ? current : availableProviders[0]
    ));
  }, [availableProviders]);

  const connectTo = useCallback(async (workspace: WorkspaceRef) => {
    setSaving(true);
    setError(null);
    try {
      const response = await fetch("/api/workspaces/connect", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider: workspace.provider,
          resourceId: workspace.resourceId,
        }),
      });
      const data = await parseJson<{ workspace: WorkspaceRef }>(response);
      onConnected(data.workspace || workspace);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to connect workspace");
    } finally {
      setSaving(false);
    }
  }, [onClose, onConnected]);

  const createWorkspace = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await fetch("/api/workspaces/create", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider: createProvider,
          projectId: projectForRequests,
          shared: createShared,
        }),
      });
      const data = await parseJson<{ workspace: WorkspaceRef }>(response);
      onConnected(data.workspace);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create workspace");
    } finally {
      setSaving(false);
    }
  }, [createProvider, createShared, onClose, onConnected, projectForRequests]);

  const projectRows = useMemo(
    () => searchResults.filter((row) => row.ownerUserId !== ""),
    [searchResults],
  );
  const createIsRecommended = mine.length === 0 && !selectedWorkspace;

  return (
    <Modal
      open={visible}
      onClose={onClose}
      labelledBy="team-workspace-dialog-title"
      className="max-w-3xl overflow-hidden p-0"
    >
        <div className="flex items-center justify-between border-b border-line-faint px-5 py-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-muted">Team Workspace</p>
            <h2 id="team-workspace-dialog-title" className="font-display text-xl text-ink">Connect Workspace</h2>
          </div>
          <Button
            variant="ghost"
            compact
            onClick={onClose}
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        <div className="border-b border-line-faint px-5 py-3 flex items-center gap-2">
          {([
            ["mine", "My Workspaces"],
            ["project", "Project Workspaces"],
            ["create", "Create Workspace"],
          ] as const).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setTab(value)}
              aria-pressed={tab === value}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-calm ${
                tab === value
                  ? "border border-ink bg-ink text-primary shadow-soft"
                  : createIsRecommended && value === "create"
                    ? "border border-fwf-yellow/35 bg-transparent text-fwf-yellow hover:border-fwf-yellow/60 hover:bg-surface-hover"
                    : "border border-line-faint text-ink-soft hover:border-accent-calm hover:bg-surface-hover hover:text-accent-calm"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="px-5 py-4 space-y-4">
          {tab === "project" ? (
            <Card className="p-3">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-muted">Project Scope</p>
                  <p className="text-sm text-ink-soft">Search workspaces shared with the selected project.</p>
                </div>
                {availableProviders.length === 1 ? (
                  <span className="rounded-full border border-accent-calm px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-accent-calm">
                    {providerLabel(availableProviders[0])} mode
                  </span>
                ) : null}
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <Field label="Project">
                  <Select
                    value={projectForRequests}
                    onChange={(e) => setSelectedProject(e.target.value)}
                  >
                    {projects.length === 0 ? <option value="">No project</option> : null}
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Search">
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="User or workspace"
                  />
                </Field>
                {availableProviders.length > 1 ? (
                  <Field label="Provider">
                    <Select
                      value={providerFilter}
                      onChange={(e) => setProviderFilter(e.target.value as "all" | WorkspaceProvider)}
                    >
                      <option value="all">All providers</option>
                      <option value="sprites">Sprites</option>
                      <option value="gcp_compute">GCP Compute</option>
                    </Select>
                  </Field>
                ) : null}
              </div>
            </Card>
          ) : null}

          {selectedWorkspace ? (
            <Status tone="success">
              Selected: {providerLabel(selectedWorkspace.provider)} · {workspaceDisplayName(selectedWorkspace)}
            </Status>
          ) : null}

          {loading ? <Status live>Loading workspaces…</Status> : null}
          {error ? <Status tone="danger" alert>{error}</Status> : null}

          {tab === "mine" ? (
            <div className="max-h-[360px] overflow-y-auto space-y-2 pr-1">
              {mine.length === 0 ? (
                <div className="rounded-lg border border-dashed border-line-faint bg-surface px-4 py-5 text-center">
                  <p className="font-display text-lg text-ink">No personal workspaces yet</p>
                  <p className="mt-2 text-sm text-ink-soft">
                    Start with a fresh cloud workspace and it will appear here for reconnecting later.
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => setTab("create")}
                    className="mt-4"
                  >
                    Create a Workspace
                  </Button>
                </div>
              ) : mine.map((row) => {
                const ownerEmail = typeof row.meta?.ownerEmail === "string" && row.meta.ownerEmail.trim() ? row.meta.ownerEmail.trim() : "";
                return (
                <div key={`${row.provider}:${row.resourceId}`} className="rounded-lg border border-line-faint bg-surface px-3 py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-ink">{workspaceDisplayName(row)}</p>
                    {ownerEmail && <p className="text-[11px] text-ink-muted">{ownerEmail}</p>}
                    <p className="font-mono text-[11px] text-ink-muted break-all">{row.resourceId}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full border border-line px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-soft">
                      {providerLabel(row.provider)}
                    </span>
                    <Button
                      variant="primary"
                      compact
                      onClick={() => void connectTo(row)}
                      disabled={saving}
                    >
                      Connect
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>
          ) : null}

          {tab === "project" ? (
            <div className="max-h-[360px] overflow-y-auto space-y-2 pr-1">
              {projectRows.length === 0 ? (
                <p className="text-sm text-ink-muted">No shared project workspaces found.</p>
              ) : projectRows.map((row) => {
                const ownerEmail = typeof row.meta?.ownerEmail === "string" && row.meta.ownerEmail.trim() ? row.meta.ownerEmail.trim() : "";
                return (
                <div key={`${row.provider}:${row.resourceId}`} className="rounded-lg border border-line-faint bg-surface px-3 py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-ink">{workspaceDisplayName(row)}</p>
                    {ownerEmail && <p className="text-[11px] text-ink-muted">{ownerEmail}</p>}
                    <p className="font-mono text-[11px] text-ink-muted break-all">{row.resourceId}</p>
                    <p className="text-[11px] text-ink-muted">Projects: {row.projectIds.join(", ") || "none"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full border border-line px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-soft">
                      {providerLabel(row.provider)}
                    </span>
                    <Button
                      variant="primary"
                      compact
                      onClick={() => void connectTo(row)}
                      disabled={saving}
                    >
                      Connect
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>
          ) : null}

          {tab === "create" ? (
            <Card className="relative overflow-hidden p-5">
              <div className="absolute -right-12 top-0 h-36 w-36 rounded-full bg-fwf-sky/10 blur-3xl" />
              <div className="relative space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="max-w-xl">
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-fwf-yellow">Fresh Workspace</p>
                    <h3 className="mt-2 font-display text-2xl text-ink">Launch a clean environment for this project</h3>
                    <p className="mt-2 text-sm leading-6 text-ink-soft">
                      Create a new cloud workspace, attach it to a project, and make it available for reconnecting later.
                    </p>
                  </div>
                  <span className="rounded-full border border-accent-calm px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-accent-calm">
                    {providerLabel(createProvider)}
                  </span>
                </div>

                <div className="grid gap-3 lg:grid-cols-[1.3fr_1fr]">
                  <div className="rounded-lg border border-line-faint bg-surface-hover p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-ink-muted">Provider</p>
                    {availableProviders.length === 1 ? (
                      <div className="mt-3 rounded-lg border border-line bg-surface-hover px-4 py-4">
                        <p className="font-display text-lg text-ink">{providerLabel(availableProviders[0])}</p>
                        <p className="mt-1 text-xs leading-relaxed text-ink-soft">
                          This deployment is configured for {providerLabel(availableProviders[0])}, so new workspaces use that provider.
                        </p>
                      </div>
                    ) : (
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        {availableProviders.map((provider) => {
                          const selected = createProvider === provider;
                          return (
                            <button
                              key={provider}
                              type="button"
                              onClick={() => setCreateProvider(provider)}
                              aria-pressed={selected}
                              className={`rounded-lg border px-4 py-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-calm ${
                                selected
                                  ? "border-accent-calm bg-surface-hover shadow-hairline"
                                  : "border-line-faint bg-surface hover:border-accent-calm hover:bg-surface-hover"
                              }`}
                            >
                              <p className="font-display text-lg text-ink">{providerLabel(provider)}</p>
                              <p className="mt-1 text-xs leading-relaxed text-ink-soft">
                                {provider === "gcp_compute"
                                  ? "Persistent compute runtime with GCP-backed storage."
                                  : provider === "e2b"
                                    ? "Isolated E2B sandbox runtime."
                                    : provider === "daytona"
                                      ? "Isolated Daytona sandbox runtime."
                                      : "Lightweight sandbox for quick setup and shared workspace flows."}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="space-y-4 rounded-lg border border-line-faint bg-surface-hover p-4">
                    <Field label="Project">
                      <Select
                        value={projectForRequests}
                        onChange={(e) => setSelectedProject(e.target.value)}
                      >
                        {projects.length === 0 ? <option value="">No project</option> : null}
                        {projects.map((project) => (
                          <option key={project.id} value={project.id}>{project.name}</option>
                        ))}
                      </Select>
                    </Field>

                    <Checkbox
                      checked={createShared}
                      onChange={(e) => setCreateShared(e.target.checked)}
                      label={(
                        <span>
                          <span className="block font-semibold text-ink">Share with project members</span>
                          <span className="mt-1 block text-xs leading-5 text-ink-muted">
                            Team members on this project can discover and reconnect to the workspace later.
                          </span>
                        </span>
                      )}
                    />

                    <ModalActions>
                      <Button
                        variant="primary"
                        onClick={() => void createWorkspace()}
                        disabled={saving}
                        loading={saving}
                        className="w-full justify-center"
                      >
                        {saving ? "Creating Workspace…" : "Create Workspace"}
                      </Button>
                    </ModalActions>
                  </div>
                </div>
              </div>
            </Card>
          ) : null}
        </div>
    </Modal>
  );
}
